import { riskRequestParams } from '../../app/pages/vulnerabilities/lib/riskFilter'
import { useMemo } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { vulnerabilitiesApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'sonner'
import type { VulnScanObject } from '../../app/modules/types/vulnerabilities'
import { mergeTimelinePages } from '../../app/pages/vulnerabilities/lib/timelineEvents'
import { mergeCommentPages } from '../../app/pages/vulnerabilities/lib/commentTree'
import { apiClient } from '../../services/client'

/** Backend max page_size is 100 (default 20); take the max to minimise round trips. */
const TIMELINE_PAGE_SIZE = 100

export function useVulnerabilities() {
  const orgId = useAuthStore((s) => s.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()
  return useQuery({
    queryKey: queryKeys.vulnerabilities.list(orgId, params),
    // `risk` is a UI-only grouping; the endpoint takes risk_min / risk_max.
    queryFn: () => vulnerabilitiesApi.getAll(riskRequestParams(params)),
    enabled: !!orgId,
  })
}

export function useVulnerabilityFilterOptions() {
  const orgId = useAuthStore((s) => s.orgId)
  return useQuery({
    queryKey: queryKeys.vulnerabilities.filterOptions(orgId),
    queryFn: () => vulnerabilitiesApi.getFilterOptions(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
  })
}

export function useVulnerabilityDetail(id: string | null) {
  const orgId = useAuthStore((s) => s.orgId)
  return useQuery({
    queryKey: queryKeys.vulnerabilities.detail(orgId, id || ''),
    queryFn: () => vulnerabilitiesApi.getById(id!),
    enabled: !!id && !!orgId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

// Two-stage PoC fetch: pass the presigned URL carried by an OCCURRENCE
// (`occurrence.poc`) and this resolves its evidence payload. Generic over the
// URL, so each occurrence row can drive its own instance.
export function useVulnerabilityPoC(pocUrl: string | null) {
  const orgId = useAuthStore((s) => s.orgId)
  return useQuery({
    queryKey: queryKeys.vulnerabilities.poc(orgId, pocUrl || ''),
    queryFn: async (): Promise<VulnScanObject> => {
      const { data } = await apiClient.get(pocUrl!)
      return data
    },
    enabled: !!pocUrl && !!orgId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

// NOTE: occurrence history is served by the timeline now (each `detected` event
// embeds its occurrence, §7), so nothing pages `/occurrences/` from the UI.
// `vulnerabilitiesApi.getOccurrences` stays available for `?ids=` deep-links (§4b).

/**
 * The Finding's comment thread, paged.
 *
 * An infinite query rather than a page-number query: a thread reads as one
 * continuous list, and the previous `cpage` search param lived in the shared
 * URL, so it leaked across findings when the drawer was closed and a different
 * row opened. Mirrors useVulnerabilityTimeline.
 */
export function useVulnerabilityComments(findingId: string | null) {
  const orgId = useAuthStore((s) => s.orgId)

  const query = useInfiniteQuery({
    queryKey: queryKeys.vulnerabilities.comments(orgId, findingId || '', ''),
    queryFn: ({ pageParam }) => vulnerabilitiesApi.getComments(findingId!, `page=${pageParam}`),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.total_pages ? lastPage.current_page + 1 : undefined,
    enabled: !!findingId && !!orgId,
  })

  const comments = useMemo(
    () => mergeCommentPages((query.data?.pages ?? []).map((p) => p.results)),
    [query.data],
  )

  return {
    comments,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: !!query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
  }
}

function useInvalidateComments(findingId: string) {
  const orgId = useAuthStore((s) => s.orgId)
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({
      predicate: (q) =>
        q.queryKey.includes('comments') && q.queryKey.includes(findingId),
    })
    qc.invalidateQueries({ predicate: (q) => q.queryKey.includes('timeline') })
  }
}

export function useAddVulnComment(findingId: string) {
  const invalidate = useInvalidateComments(findingId)
  return useMutation({
    mutationFn: (payload: { body: string; parent_id?: string }) =>
      vulnerabilitiesApi.addComment(findingId, payload),
    // The success toast is fired by the container, which has the intl context;
    // a hook should not own a user-visible string in a bilingual app.
    onSuccess: () => { invalidate() },
  })
}

export function useEditVulnComment(findingId: string) {
  const invalidate = useInvalidateComments(findingId)
  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      vulnerabilitiesApi.editComment(findingId, commentId, body),
    onSuccess: invalidate,
  })
}

export function useDeleteVulnComment(findingId: string) {
  const invalidate = useInvalidateComments(findingId)
  return useMutation({
    mutationFn: (commentId: string) => vulnerabilitiesApi.deleteComment(findingId, commentId),
    onSuccess: invalidate,
  })
}

/**
 * The Finding's timeline — now also its occurrence history, since each `detected`
 * event carries its evidence occurrence nested (§7). Paginated newest-first with
 * a stable id tiebreak; pages are merged and deduped (§3).
 */
export function useVulnerabilityTimeline(findingId: string | null) {
  const orgId = useAuthStore((s) => s.orgId)

  const query = useInfiniteQuery({
    queryKey: queryKeys.vulnerabilities.timeline(orgId, findingId || '', ''),
    queryFn: ({ pageParam }) =>
      vulnerabilitiesApi.getTimeline(findingId!, `page=${pageParam}&page_size=${TIMELINE_PAGE_SIZE}`),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.total_pages ? lastPage.current_page + 1 : undefined,
    enabled: !!findingId && !!orgId,
  })

  const events = useMemo(
    () => mergeTimelinePages((query.data?.pages ?? []).map((p) => p.results)),
    [query.data],
  )

  return {
    events,
    isLoading: query.isLoading,
    hasMore: !!query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
  }
}

export function useDeleteVulnerability() {
  const orgId = useAuthStore((s) => s.orgId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (findingId: string) => vulnerabilitiesApi.delete(findingId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.vulnerabilities.all(orgId) })
      toast.success('Finding deleted')
    },
  })
}
